<template>
  <div class="instant-campaign-form">
    <!-- Header -->
    <div class="instant-campaign-form__header">
      <div class="d-flex align-items-center">
        <b-icon
          icon="arrow-left-square-fill"
          class="back-arrow h3 mb-0 cursor-pointer"
          @click="$router.go(-1)"
        ></b-icon>
        <div class="ml-3">
          <h1 class="page-title">Crear Campañas Instantáneas</h1>
          <p class="page-subtitle">Inicio / Campañas Instantáneas / Crear Correo</p>
        </div>
      </div>
    </div>

    <div class="form-layout">
      <!-- Columna izquierda: Formulario -->
      <div class="form-content">

        <!-- PASO 1: Reglas de Envío -->
        <div class="form-section">
          <div class="section-header">
            <h2 class="section-title">
              <span class="step-number">1</span>
              Reglas de Envío
            </h2>
          </div>

          <div class="section-body">
            <!-- Banner informativo -->
            <div class="info-banner mb-3">
              <i class="fas fa-info-circle mr-2"></i>
              Los destinatarios están limitados a OTs de tipo <strong>"Persona"</strong>
            </div>

            <!-- Nombre Campaña -->
            <b-form-group
              label="Nombre Campaña"
              label-for="nombre"
              :state="validarCampo('nombre')"
              :invalid-feedback="errores.nombre"
              class="mb-3"
            >
              <b-form-input
                id="nombre"
                v-model="form.nombre"
                placeholder="Ingresa un Nombre para identificar esta campaña."
                maxlength="100"
                :state="validarCampo('nombre')"
              />
            </b-form-group>

            <!-- Tipo de OT -->
            <b-form-group
              label="Tipo de OT"
              :state="validarCampo('tipo_ot')"
              :invalid-feedback="errores.tipo_ot"
              class="mb-3"
            >
              <div class="tipo-ot-buttons">
                <button
                  type="button"
                  class="tipo-ot-btn"
                  :class="{ active: form.tipo_ot === 'TODAS' }"
                  @click="form.tipo_ot = 'TODAS'"
                >
                  <svg viewBox="0 0 24 24" class="tipo-ot-icon">
                    <path
                      d="M21 11C21 16.55 17.16 21.74 12 23C6.84 21.74 3 16.55 3 11V5L12 1L21 5V11Z
                         M12 21C15.75 20 19 15.54 19 11.22V6.3L12 3.18L5 6.3V11.22C5 15.54 8.25 20 12 21Z
                         M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9"
                      fill="currentColor"
                    />
                  </svg>
                  Todas
                </button>
                <button
                  type="button"
                  class="tipo-ot-btn"
                  :class="{ active: form.tipo_ot === 'PRESUPUESTO' }"
                  @click="form.tipo_ot = 'PRESUPUESTO'"
                >
                  <svg viewBox="0 0 24 24" class="tipo-ot-icon">
                    <path
                      d="M6 2C5.47 2 4.96 2.21 4.59 2.59C4.21 2.96 4 3.47 4 4V20
                         C4 20.53 4.21 21.04 4.59 21.41C4.96 21.79 5.47 22 6 22H18
                         C18.53 22 19.04 21.79 19.41 21.41C19.79 21.04 20 20.53 20 20
                         V8L14 2H6ZM6 4H13V9H18V20H6V4ZM8 12V14H16V12H8ZM8 16V18H13V16H8Z"
                      fill="currentColor"
                    />
                  </svg>
                  Presupuesto
                </button>
                <button
                  type="button"
                  class="tipo-ot-btn"
                  :class="{ active: form.tipo_ot === 'GARANTIA' }"
                  @click="form.tipo_ot = 'GARANTIA'"
                >
                  <svg viewBox="0 0 24 24" class="tipo-ot-icon">
                    <path
                      d="M21 11C21 16.55 17.16 21.74 12 23C6.84 21.74 3 16.55 3 11V5L12 1L21 5V11Z
                         M12 21C15.75 20 19 15.54 19 11.22V6.3L12 3.18L5 6.3V11.22C5 15.54 8.25 20 12 21Z
                         M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9"
                      fill="currentColor"
                    />
                  </svg>
                  Garantía
                </button>
              </div>
            </b-form-group>

            <!-- Condiciones de Filtrado -->
            <div class="condiciones-section">
              <h3 class="condiciones-title">Condiciones de Filtrado</h3>

              <!-- Lista de condiciones activas -->
              <div
                v-for="(condicion, index) in form.condiciones"
                :key="condicion.tipo"
                class="condicion-row mb-2"
              >
                <label class="condicion-label">{{ getCondicionLabel(condicion.tipo) }}</label>
                <div class="condicion-input-group">
                  <b-form-input
                    v-model.number="condicion.valor"
                    type="number"
                    min="1"
                    class="condicion-input"
                    placeholder="Ingresa N° de días"
                  />
                  <button
                    type="button"
                    class="btn-delete-condicion"
                    @click="eliminarCondicion(index)"
                    title="Eliminar condición"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="11.5" fill="#FF3838"/>
                      <path
                        d="M18 4.88889H15L14.1429 4H9.85714L9 4.88889H6V6.66667H18
                           M6.85714 18.2222C6.85714 18.6937 7.03775 19.1459
                           7.35925 19.4793C7.68074 19.8127 8.11677 20 8.57143 20H15.4286
                           C15.8832 20 16.3193 19.8127 16.6408 19.4793
                           C16.9622 19.1459 17.1429 18.6937 17.1429 18.2222
                           V7.55556H6.85714V18.2222Z"
                        fill="#F5F6FA"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Agregar condición -->
              <b-dropdown
                v-if="condicionesDisponibles.length > 0"
                variant="link"
                no-caret
                class="dropdown-agregar-condicion mt-1"
              >
                <template #button-content>
                  <i class="fas fa-plus-circle mr-1"></i>
                  Agregar Condiciones de Filtrado
                </template>
                <b-dropdown-item
                  v-for="tipo in condicionesDisponibles"
                  :key="tipo.value"
                  @click="agregarCondicion(tipo.value)"
                >
                  {{ tipo.label }}
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <!-- Modelo Equipo -->
            <b-form-group label="Modelo Equipo" class="mb-3 mt-3">
              <div class="modelo-select-wrapper">
                <b-form-select
                  v-model="modeloSeleccionado"
                  :options="opcionesModelos"
                  @change="agregarModelo"
                >
                  <template #first>
                    <b-form-select-option :value="null">Seleccionar modelo...</b-form-select-option>
                  </template>
                </b-form-select>
              </div>
              <!-- Chips de modelos seleccionados -->
              <div v-if="form.modelos.length > 0" class="modelos-chips mt-2">
                <span
                  v-for="(modelo, index) in form.modelos"
                  :key="index"
                  class="modelo-chip"
                >
                  {{ modelo.label }}
                  <button type="button" class="chip-remove" @click="quitarModelo(index)">×</button>
                </span>
              </div>
            </b-form-group>

            <!-- Buscar OTs -->
            <div class="calcular-section mt-3">
              <b-button
                variant="warning"
                class="btn-calcular"
                :disabled="calculando"
                @click="buscarOts"
              >
                <b-spinner v-if="calculando" small class="mr-2"></b-spinner>
                <i v-else class="fas fa-search mr-2"></i>
                {{ calculando ? 'Buscando...' : 'Buscar OTs' }}
              </b-button>

              <!-- Resultado del cálculo -->
              <div v-if="resultadoDestinatarios !== null" class="resultado-destinatarios mt-2">
                <div
                  :class="['resultado-banner', resultadoDestinatarios > 0 ? 'resultado-success' : 'resultado-empty']"
                >
                  <i :class="['fas mr-2', resultadoDestinatarios > 0 ? 'fa-check-circle' : 'fa-exclamation-circle']"></i>
                  <span v-if="resultadoDestinatarios > 0">
                    Se han encontrado <strong>{{ resultadoDestinatarios }}</strong> destinatarios para las condiciones seleccionadas.
                    <b-link class="ml-2" @click="mostrarTablaOts = !mostrarTablaOts">
                      {{ mostrarTablaOts ? 'Ocultar' : 'Ver detalle' }}
                    </b-link>
                  </span>
                  <span v-else>
                    No se encontraron destinatarios con las condiciones seleccionadas.
                  </span>
                </div>

                <!-- Tabla colapsable de OTs -->
                <b-collapse v-model="mostrarTablaOts" class="mt-2">
                  <b-table
                    v-if="otsFiltradas.length > 0"
                    :items="otsFiltradas"
                    :fields="camposOts"
                    small
                    bordered
                    responsive
                    class="ots-results-table"
                    show-empty
                    empty-text="Sin resultados"
                  />
                </b-collapse>
              </div>
            </div>
          </div>
        </div>

        <!-- PASO 2: Contenido Correo -->
        <div class="form-section mt-4">
          <div class="section-header">
            <h2 class="section-title">
              <span class="step-number">2</span>
              Contenido Correo
            </h2>
          </div>

          <div class="section-body">
            <!-- Asunto -->
            <b-form-group
              label="Asunto"
              label-for="asunto"
              :state="validarCampo('asunto')"
              :invalid-feedback="errores.asunto"
              class="mb-3"
            >
              <b-form-input
                id="asunto"
                v-model="form.asunto"
                placeholder="Ingresa el asunto del correo"
                maxlength="200"
                :state="validarCampo('asunto')"
                @focus="editorActivo = 'asunto'"
              />
            </b-form-group>

            <!-- Cuerpo del Correo -->
            <b-form-group
              label="Cuerpo del Correo"
              :state="validarCampo('cuerpo')"
              :invalid-feedback="errores.cuerpo"
            >
              <EmailEditor
                ref="editor"
                v-model="form.cuerpo"
                placeholder="Escribe el contenido del correo..."
                :min-height="200"
                @focus="editorActivo = 'editor'"
              />
            </b-form-group>

            <!-- Botón Enviar -->
            <div class="enviar-section mt-4">
              <b-button
                variant="warning"
                size="lg"
                class="btn-enviar"
                :disabled="enviando || resultadoDestinatarios === null || resultadoDestinatarios === 0"
                @click="confirmarEnvio"
              >
                <b-spinner v-if="enviando" small class="mr-2"></b-spinner>
                <i v-else class="fas fa-paper-plane mr-2"></i>
                {{ enviando ? 'Enviando...' : 'Enviar Campaña' }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna derecha: Panel de etiquetas -->
      <div class="tags-sidebar">
        <TagsPanel @tag-selected="insertarEtiqueta" />
      </div>
    </div>

    <!-- Modal de confirmación de envío -->
    <b-modal
      v-model="showConfirmModal"
      title="Confirmar Envío de Campaña"
      no-close-on-backdrop
      @ok="doEnviarCampana"
      ok-title="Enviar"
      ok-variant="warning"
      cancel-title="Cancelar"
    >
      <p>
        ¿Estás seguro de que deseas enviar la campaña
        <strong>{{ form.nombre || 'sin nombre' }}</strong>?
      </p>
      <p v-if="resultadoDestinatarios !== null" class="text-muted">
        Esta acción enviará el correo a <strong>{{ resultadoDestinatarios }}</strong> destinatarios.
      </p>
      <p class="text-danger mb-0">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Esta acción no se puede deshacer.
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EmailEditor from '@/components/Campaign/EmailEditor.vue';
import TagsPanel from '@/components/Campaign/TagsPanel.vue';

const CONDICION_TIPOS = [
  { value: 'fecha_cierre_mayor_a', label: 'Fecha de cierre mayor a' },
  { value: 'fecha_compra_mayor_a', label: 'Fecha de compra mayor a' },
];

export default {
  name: 'InstantCampaignForm',
  components: { EmailEditor, TagsPanel },
  data() {
    return {
      form: {
        nombre: '',
        tipo_ot: 'TODAS',
        condiciones: [],
        modelos: [],
        asunto: '',
        cuerpo: '',
      },
      errores: {},
      calculando: false,
      enviando: false,
      resultadoDestinatarios: null,
      otsFiltradas: [],
      mostrarTablaOts: false,
      modeloSeleccionado: null,
      editorActivo: null,
      showConfirmModal: false,
      campanaId: null,
      camposOts: [
        { key: 'numero_ot', label: 'N° OT' },
        { key: 'cliente_nombre', label: 'Cliente' },
        { key: 'destinatario', label: 'Destinatario' },
        { key: 'tipo_ot', label: 'Tipo' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'fecha_cierre', label: 'Fecha Cierre' },
      ],
    };
  },
  computed: {
    ...mapGetters('EquipmentStore', ['models']),
    opcionesModelos() {
      return this.models.map((m) => ({
        value: { value: m.id, label: m.model_name },
        text: m.model_name,
      }));
    },
    condicionesDisponibles() {
      const activas = this.form.condiciones.map((c) => c.tipo);
      return CONDICION_TIPOS.filter((t) => !activas.includes(t.value));
    },
  },
  created() {
    this.getModels();
  },
  methods: {
    ...mapActions('EquipmentStore', ['getModels']),
    ...mapActions('InstantCampaign', ['filtrarOts', 'enviarCampana']),

    getCondicionLabel(tipo) {
      return CONDICION_TIPOS.find((t) => t.value === tipo)?.label || tipo;
    },

    agregarCondicion(tipo) {
      this.form.condiciones.push({ tipo, valor: null });
    },

    eliminarCondicion(index) {
      this.form.condiciones.splice(index, 1);
    },

    agregarModelo(modelo) {
      if (!modelo) return;
      const yaAgregado = this.form.modelos.some((m) => m.value === modelo.value);
      if (!yaAgregado) {
        this.form.modelos.push(modelo);
      }
      this.$nextTick(() => { this.modeloSeleccionado = null; });
    },

    quitarModelo(index) {
      this.form.modelos.splice(index, 1);
    },

    insertarEtiqueta(tag) {
      if (this.editorActivo === 'editor' && this.$refs.editor) {
        this.$refs.editor.insertarEtiqueta(tag);
      } else if (this.editorActivo === 'asunto') {
        this.form.asunto += tag;
      } else if (this.$refs.editor) {
        this.$refs.editor.insertarEtiqueta(tag);
      }
    },

    validarCampo(campo) {
      if (this.errores[campo]) return false;
      return null;
    },

    validarFormulario() {
      this.errores = {};
      if (!this.form.nombre.trim()) {
        this.errores.nombre = 'El nombre de la campaña es obligatorio.';
      }
      if (!this.form.asunto.trim()) {
        this.errores.asunto = 'El asunto es obligatorio.';
      }
      if (!this.form.cuerpo || this.form.cuerpo === '<p><br></p>') {
        this.errores.cuerpo = 'El cuerpo del correo es obligatorio.';
      }
      return Object.keys(this.errores).length === 0;
    },

    async buscarOts() {
      this.calculando = true;
      this.resultadoDestinatarios = null;
      this.otsFiltradas = [];
      this.mostrarTablaOts = false;
      try {
        const filtros = {
          tipo_ot: this.form.tipo_ot,
          condiciones: this.form.condiciones,
          modelos: this.form.modelos.map((m) => m.value),
        };
        const resultado = await this.filtrarOts(filtros);
        this.resultadoDestinatarios = resultado.total != null ? resultado.total : 0;
        this.otsFiltradas = resultado.ots || [];
      } catch {
        this.resultadoDestinatarios = 0;
      } finally {
        this.calculando = false;
      }
    },

    confirmarEnvio() {
      if (!this.validarFormulario()) return;
      this.showConfirmModal = true;
    },

    async doEnviarCampana() {
      this.enviando = true;
      try {
        const datos = {
          nombre: this.form.nombre,
          tipo_ot: this.form.tipo_ot,
          condiciones: this.form.condiciones,
          modelos: this.form.modelos.map((m) => m.value),
          asunto: this.form.asunto,
          cuerpo: this.form.cuerpo,
        };
        await this.enviarCampana(datos);
        this.$router.push({ name: 'instant-campaign-list' });
      } catch {
        this.$bvToast.toast('Error al enviar la campaña. Inténtalo nuevamente.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.instant-campaign-form {
  padding: 24px;
  background: #F5F6FA;
  min-height: 100vh;
}

.instant-campaign-form__header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #23201C;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 13px;
  color: #525252;
  margin: 0;
}

.back-arrow {
  color: #FECE2F;
  cursor: pointer;
}

.back-arrow:hover {
  color: #23201C;
}

.form-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.form-content {
  flex: 1;
  min-width: 0;
}

.tags-sidebar {
  width: 260px;
  flex-shrink: 0;
}

.form-section {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #23201C;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #FECE2F;
  color: #23201C;
  font-weight: 700;
  border-radius: 50%;
  font-size: 14px;
  flex-shrink: 0;
}

/* Banner informativo */
.info-banner {
  background: rgba(29, 78, 216, 0.08);
  border: 1px solid rgba(29, 78, 216, 0.25);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #1D4ED8;
}

/* Tipo OT buttons */
.tipo-ot-buttons {
  display: flex;
  flex-direction: row;
  gap: 20px;
  max-width: 503px;
}

.tipo-ot-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 36px;
  border: 1px solid #525252;
  background: rgba(82, 82, 82, 0.35);
  color: #525252;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 400;
  transition: all 0.15s ease;
}

.tipo-ot-btn:hover {
  border-color: #FECE2F;
  background: rgba(254, 206, 47, 0.2);
}

.tipo-ot-btn.active {
  border-color: #FECE2F;
  background: rgba(254, 206, 47, 0.3);
  color: #23201C;
  font-weight: 400;
}

.tipo-ot-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

/* Condiciones */
.condiciones-section {
  border-top: 1px solid #F0F0F0;
  padding-top: 16px;
}

.condiciones-title {
  font-size: 14px;
  font-weight: 600;
  color: #23201C;
  margin: 0 0 12px 0;
}

.condicion-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.condicion-label {
  font-size: 13px;
  font-weight: 600;
  color: #23201C;
  margin: 0;
}

.condicion-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.condicion-input {
  flex: 1;
}

.btn-delete-condicion {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.btn-delete-condicion:hover {
  opacity: 0.8;
}

::v-deep .dropdown-agregar-condicion > .btn {
  color: #1D4ED8;
  font-size: 13px;
  font-weight: 600;
  padding: 0;
  text-decoration: none;
}

::v-deep .dropdown-agregar-condicion > .btn:hover {
  color: #1e40af;
  text-decoration: underline;
}

/* Modelo chips */
.modelos-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.modelo-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #F0F0F0;
  color: #23201C;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.chip-remove {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #525252;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.chip-remove:hover {
  color: #EF4444;
}

/* Calcular destinatarios */
.btn-calcular {
  font-weight: 600;
}

.resultado-banner {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
}

.resultado-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.35);
  color: #166534;
}

.resultado-empty {
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.35);
  color: #854d0e;
}

/* Enviar */
.enviar-section {
  display: flex;
  justify-content: flex-end;
}

.btn-enviar {
  font-weight: 700;
  padding: 10px 32px;
}

@media (max-width: 991px) {
  .form-layout {
    flex-direction: column;
  }

  .tags-sidebar {
    width: 100%;
  }
}
</style>
